import { useState, useCallback, DragEvent, ChangeEvent } from 'react';

interface UseDragDropOptions {
  accept?: string[];
  onFile?: (file: File) => void;
}

export function useDragDrop({ accept, onFile }: UseDragDropOptions = {}) {
  const [isDragging, setIsDragging] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);

  const isAccepted = (file: File): boolean => {
    if (!accept || accept.length === 0) return true;
    return accept.some((ext) => file.name.endsWith(ext));
  };

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file && isAccepted(file)) {
        setDroppedFile(file);
        onFile?.(file);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onFile]
  );

  const handleFileInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setDroppedFile(file);
        onFile?.(file);
      }
    },
    [onFile]
  );

  const clearFile = () => setDroppedFile(null);

  return {
    isDragging,
    droppedFile,
    clearFile,
    dragProps: {
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
    inputProps: {
      onChange: handleFileInput,
    },
  };
}
